import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import CopyWorkOrderDialog from './CopyWorkOrderDialog';
import { PRIORITY_OPTIONS, normalizePriority } from '../lib/priority';

const blankForm = {
  title: '',
  description: '',
  status: 'not_started',
  priority: '',
  due_date: '',
  assigned_to: '',
  notes: '',
  frequency_days: '',
  frequency_hours: '',
  hours_due_at: '',
  reminder_enabled: false,
};

const AddWorkOrderDialog = ({ open, onClose, machineryId, onSuccess, initialValues = null, sourceRequestId = null }) => {
  const [formData, setFormData] = useState(blankForm);
  const [loading, setLoading] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);

  useEffect(() => {
    if (open && initialValues) setFormData({ ...blankForm, ...initialValues });
  }, [open, initialValues]);

  const handleCopyFrom = (sourceWO) => {
    // Prefill from another WO. Status resets, due_date + assigned_to stay blank.
    setFormData({
      title: sourceWO.title || '',
      description: sourceWO.description || '',
      status: 'not_started',
      priority: sourceWO.priority || '',
      due_date: '',
      assigned_to: '',
      notes: sourceWO.notes || '',
      frequency_days:
        sourceWO.frequency_days != null ? String(sourceWO.frequency_days) : '',
      frequency_hours:
        sourceWO.frequency_hours != null ? String(sourceWO.frequency_hours) : '',
      hours_due_at:
        sourceWO.hours_due_at != null ? String(sourceWO.hours_due_at) : '',
      reminder_enabled: !!sourceWO.reminder_enabled,
    });
    toast.success(`Copied from "${sourceWO.title}"`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Set due date to 11:59 PM on the selected date
      let dueDateISO = null;
      if (formData.due_date) {
        const [year, month, day] = formData.due_date.split('-').map(Number);
        const dueDate = new Date(year, month - 1, day, 23, 59, 59);
        dueDateISO = dueDate.toISOString();
      }

      const submitData = {
        machinery_id: machineryId,
        source_request_id: sourceRequestId,
        title: formData.title,
        description: formData.description || null,
        status: formData.status,
        priority: formData.priority || null,
        due_date: dueDateISO,
        assigned_to: formData.assigned_to || null,
        notes: formData.notes || null,
        frequency_days: formData.frequency_days
          ? parseInt(formData.frequency_days, 10)
          : null,
        frequency_hours: formData.frequency_hours
          ? parseInt(formData.frequency_hours, 10)
          : null,
        hours_due_at: formData.hours_due_at !== ''
          ? parseFloat(formData.hours_due_at)
          : null,
        reminder_enabled: !!formData.reminder_enabled,
      };

      const res = await axios.post(`${API}/work-orders`, submitData);
      toast.success('Work order created successfully!');
      onSuccess(res.data);
      setFormData(blankForm);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create work order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Work Order</DialogTitle>
            <DialogDescription>Add upcoming work to be scheduled</DialogDescription>
          </DialogHeader>

          <div className="flex justify-end -mt-2 mb-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowCopyDialog(true)}
              data-testid="copy-from-wo-btn"
            >
              Copy from…
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                data-testid="work-order-title-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Add gas filter to Fill Station 1"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                data-testid="work-order-description-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed description of work to be done"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  data-testid="work-order-status-select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <select
                  id="priority"
                  data-testid="work-order-priority-select"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">Select priority</option>
                  {PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  data-testid="work-order-due-date-input"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="assigned_to">Assigned To</Label>
                <Input
                  id="assigned_to"
                  data-testid="work-order-assigned-input"
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  placeholder="Technician name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="frequency_days">Maintenance Frequency (days)</Label>
                <Input
                  id="frequency_days"
                  data-testid="work-order-frequency-input"
                  type="number"
                  min="1"
                  value={formData.frequency_days}
                  onChange={(e) => setFormData({ ...formData, frequency_days: e.target.value })}
                  placeholder="e.g., 30, 90, 365"
                />
                <p className="text-xs text-slate-500">
                  If set, a fresh work order spawns when this one is completed.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reminder_enabled" className="block">Enable maintenance reminders</Label>
                <div className="h-9 flex items-center">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      id="reminder_enabled"
                      data-testid="work-order-reminder-toggle"
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!formData.reminder_enabled}
                      onChange={(e) => setFormData({ ...formData, reminder_enabled: e.target.checked })}
                    />
                    <span className="text-sm text-slate-700">Remind me about this work order</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hours_due_at">Due at Meter Reading (hrs)</Label>
                <Input
                  id="hours_due_at"
                  data-testid="work-order-hours-due-input"
                  type="number"
                  min="0"
                  step="0.1"
                  value={formData.hours_due_at}
                  onChange={(e) => setFormData({ ...formData, hours_due_at: e.target.value })}
                  placeholder="e.g., 24000"
                />
                <p className="text-xs text-slate-500">
                  WO is also due when the equipment meter hits this reading — whichever comes first.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="frequency_hours">Frequency (run hours)</Label>
                <Input
                  id="frequency_hours"
                  data-testid="work-order-frequency-hours-input"
                  type="number"
                  min="1"
                  value={formData.frequency_hours}
                  onChange={(e) => setFormData({ ...formData, frequency_hours: e.target.value })}
                  placeholder="e.g., 4000, 8000"
                />
                <p className="text-xs text-slate-500">
                  On completion the next WO is due at current meter + this many hours.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                data-testid="work-order-notes-textarea"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes or requirements"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-work-order-btn">
                {loading ? 'Creating...' : 'Create Work Order'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <CopyWorkOrderDialog
        open={showCopyDialog}
        onClose={() => setShowCopyDialog(false)}
        onSelect={handleCopyFrom}
        excludeMachineryId={machineryId}
      />
    </>
  );
};

export default AddWorkOrderDialog;
